import { useState } from "react";
import styled from "styled-components";
import { Z_INDEX } from "@constants";
import { theme } from "@utilities";
import { signupOfferCopy } from "src/Module/Checkout/signupOfferConfig";
import { ISignupNames } from "src/Module/Checkout/socialSignupNames";

interface SignupNamesPromptProps {
    /** Called with the names, or null if the visitor chose to go without. */
    onResolve: (names: ISignupNames | null) => void;
}

/**
 * Asks a new social account for its name before the points-agent upgrade.
 *
 * Apple never tells us one: the name travels in the first authorisation's form
 * POST, which goes to the backend, and the `id_token` we are handed back carries
 * no name at all. The upgrade is what mints the account's permanent branch
 * identity, so without this the visitor is filed as "Guest Traveller" for good.
 *
 * Skipping is allowed and costs only the upgrade — see resolveUpgradeNames — so
 * the copy says plainly what is given up rather than pretending the fields are
 * optional decoration.
 */
export const SignupNamesPrompt = ({ onResolve }: SignupNamesPromptProps) => {
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");

    const canSave = firstName.trim() !== "" && lastName.trim() !== "";

    return (
        <Backdrop role="dialog" aria-modal="true" aria-label={signupOfferCopy.namesPromptHeadline}>
            <Card
                onSubmit={(e) => {
                    e.preventDefault();
                    if (!canSave) return;
                    onResolve({ first_name: firstName.trim(), last_name: lastName.trim() });
                }}
            >
                <Headline>{signupOfferCopy.namesPromptHeadline}</Headline>
                <Subtitle>{signupOfferCopy.namesPromptSubtitle}</Subtitle>

                <Field
                    type="text"
                    autoComplete="given-name"
                    placeholder="First name"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    autoFocus
                />
                <Field
                    type="text"
                    autoComplete="family-name"
                    placeholder="Last name"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                />

                <SaveButton type="submit" disabled={!canSave}>
                    {signupOfferCopy.namesPromptButton}
                </SaveButton>
                <SkipLink type="button" onClick={() => onResolve(null)}>
                    {signupOfferCopy.namesPromptSkip}
                </SkipLink>
            </Card>
        </Backdrop>
    );
};

const Backdrop = styled.div`
    position: fixed;
    inset: 0;
    z-index: ${Z_INDEX.EMERGENCY};
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 16px;
    background: rgba(19, 18, 20, 0.45);
`;

const Card = styled.form`
    display: flex;
    flex-direction: column;
    width: 100%;
    max-width: 380px;
    padding: 28px 24px;
    border-radius: ${theme.borderRadius.small};
    background: ${theme.colors.white};
    box-shadow: 0 10px 32px rgba(0, 0, 0, 0.22);
`;

const Headline = styled.h2`
    margin: 0 0 8px;
    font-size: 22px;
    font-weight: 700;
    line-height: 1.2;
    text-align: center;
    color: ${theme.colors.black};
`;

const Subtitle = styled.p`
    margin: 0 0 16px;
    text-align: center;
    font-size: 14px;
    line-height: 1.4;
    color: ${theme.colors.gray_1};
`;

const Field = styled.input`
    width: 100%;
    height: 46px;
    padding: 0 14px;
    margin-bottom: 10px;
    font-size: 16px;
    border: 1px solid ${theme.colors.gray_5};
    border-radius: ${theme.borderRadius.small};
    background: ${theme.colors.white};

    &::placeholder {
        color: ${theme.colors.gray_3};
    }

    &:focus {
        outline: none;
        border-color: ${theme.colors.main};
    }
`;

const SaveButton = styled.button`
    width: 100%;
    height: 50px;
    margin-top: 6px;
    border: none;
    border-radius: ${theme.borderRadius.small};
    background: linear-gradient(
        90deg,
        ${theme.gradients.button[0]},
        ${theme.gradients.button[1]}
    );
    color: ${theme.colors.button_text};
    font-family: "Plus Jakarta Sans", sans-serif;
    font-size: 16px;
    font-weight: 700;
    cursor: pointer;

    &:disabled {
        opacity: 0.55;
        cursor: not-allowed;
    }
`;

const SkipLink = styled.button`
    margin-top: 14px;
    border: none;
    background: none;
    font-size: 13px;
    color: ${theme.colors.gray_1};
    text-decoration: underline;
    cursor: pointer;
`;
